import apiClient from './client'

const storageURL = import.meta.env.VITE_STORAGE_URL || 'http://localhost:84/storage'
const mediaUrl = (p) => { if (!p) return null; let s = String(p); const i = s.indexOf('/storage/'); if (i !== -1) s = s.slice(i + 9); return storageURL + '/' + s.replace(/^\/+/, '') }

// Normalise une conversation reçue de l'API vers la structure attendue par la page
const normalizeConversation = (conv) => {
  const other = conv.other_client || {}
  const fullName = [other.prenom, other.nom].filter(Boolean).join(' ') || other.email || `Client #${other.client_id}`
  const avatar = other.photo_profil
    ? (other.photo_profil.startsWith('http') ? other.photo_profil : mediaUrl(other.photo_profil))
    : null
  return {
    id:                  conv.id,
    contact_id:          other.client_id,
    contact_full_name:   fullName,
    contact_avatar:      avatar,
    contact_phone:       other.numero || other.phone || '',
    contact_email:       other.email  || '',
    unread_count:        conv.unread_count ?? 0,
    last_message:        conv.last_message?.urlArticle
                           ? (conv.last_message?.nomArticle || 'Article partagé')
                           : (conv.last_message?.message || ''),
    last_is_article:     !!conv.last_message?.urlArticle,
    last_image:          conv.last_message?.imageArticle || null,
    last_message_time:   conv.updated_at || conv.last_message?.created_at || null,
  }
}

// Normalise un message reçu de l'API
const normalizeMessage = (msg, currentUserId) => {
  const sender = msg.sender || {}
  return {
    id:               msg.id,
    sender_id:        msg.id_sender,
    receiver_id:      msg.id_receiver,
    conversation_id:  msg.id_conversa,
    message:          msg.message || '',
    urlArticle:       msg.urlArticle   || null,
    nomArticle:       msg.nomArticle   || null,
    imageArticle:     msg.imageArticle || null,
    is_mine:          String(msg.id_sender) === String(currentUserId),
    edited:           !!msg.edited,
    liked:            !!msg.liked,
    created_at:       msg.created_at,
    sender_full_name: [sender.prenom, sender.nom].filter(Boolean).join(' ') || 'Inconnu',
    sender_avatar:    sender.photo_profil
      ? (sender.photo_profil.startsWith('http') ? sender.photo_profil : mediaUrl(sender.photo_profil))
      : null,
  }
}

export const adminMessageService = {
  // ── Conversations ──────────────────────────────────────────────────────────

  /** Liste toutes les conversations de l'utilisateur connecté */
  getConversations: async () => {
    const res = await apiClient.get('/conversations')
    return {
      success: res?.success !== false,
      data: (res?.data || []).map(normalizeConversation),
    }
  },

  /** Alias utilisé par AdminMessagesPage */
  getAdminConversations: async () => {
    const res = await apiClient.get('/conversations')
    return {
      success: res?.success !== false,
      data: (res?.data || []).map(normalizeConversation),
    }
  },

  /** Crée ou retrouve une conversation avec un client cible */
  createConversation: async (clientId) => {
    const res = await apiClient.post('/conversations', { client_id: clientId })
    return {
      success: res?.success !== false,
      data: res?.data ? normalizeConversation({
        id:           res.data.id,
        other_client: res.data.client2 || res.data.client1 || {},
        unread_count: 0,
        last_message: null,
        updated_at:   res.data.created_at,
      }) : null,
    }
  },

  /** Alias utilisé par AdminMessagesPage */
  createAdminConversation: async (_userId, clientId) => {
    return adminMessageService.createConversation(clientId)
  },

  // ── Messages ───────────────────────────────────────────────────────────────

  /** Récupère les messages d'une conversation */
  getMessages: async (conversationId, currentUserId) => {
    const res = await apiClient.get('/messages', { params: { id_conversa: conversationId } })
    return {
      success: res?.success !== false,
      data: (res?.data || []).map(m => normalizeMessage(m, currentUserId)),
      partner_typing: !!res?.partner_typing,
    }
  },

  /** Signale que l'admin est en train d'écrire */
  sendTyping: (conversationId) =>
    apiClient.post('/messages/typing', { id_conversa: conversationId }),

  /** Modifie le texte d'un message (expéditeur uniquement) */
  updateMessage: async (id, message) => {
    const res = await apiClient.put(`/messages/${id}`, { message })
    return { success: res?.success !== false, data: res?.data || null }
  },

  /** Bascule le like sur un message */
  toggleLike: async (id) => {
    const res = await apiClient.post(`/messages/${id}/like`)
    return { success: res?.success !== false, liked: res?.liked }
  },

  /** Supprime un message (expéditeur uniquement) */
  deleteMessage: async (id) => {
    const res = await apiClient.delete(`/messages/${id}`)
    return { success: res?.success !== false }
  },

  /** Envoie un message à un destinataire (crée la conversation si besoin) */
  sendMessage: async (receiverId, messageText, urlArticle = null) => {
    const body = { id_receiver: receiverId, message: messageText }
    if (urlArticle) body.urlArticle = urlArticle
    const res = await apiClient.post('/messages', body)
    return {
      success: res?.success !== false,
      data:    res?.data || null,
    }
  },

  // ── Clients ────────────────────────────────────────────────────────────────

  /** Liste les clients (pour le modal nouvelle conversation) */
  getClients: (params = {}) => apiClient.get('/clients', { params }),

  /** Alias utilisé par AdminMessagesPage */
  getClientsList: async (_userId, search = '') => {
    const res = await apiClient.get('/clients', { params: search ? { search } : {} })
    const clients = res?.data || res || []
    return {
      success: true,
      data: Array.isArray(clients)
        ? clients.map(c => ({
            id:     c.client_id,
            name:   [c.prenom, c.nom].filter(Boolean).join(' '),
            email:  c.email  || '',
            phone:  c.numero || '',
            avatar: c.photo_profil
              ? (c.photo_profil.startsWith('http') ? c.photo_profil : mediaUrl(c.photo_profil))
              : null,
          }))
        : [],
    }
  },

  // ── Divers ─────────────────────────────────────────────────────────────────

  getUnreadCount: () => apiClient.get('/messages/unread'),

  markRead: (conversationId) =>
    apiClient.post('/messages/mark-read', { id_conversa: conversationId }),

  getConversationById: (id) => apiClient.get(`/conversations/${id}`),
}

export default adminMessageService
